import { ApiClient } from '../utils/apiClient';
import { logoutCleanupService } from './logoutCleanupService';
import { sessionHeartbeatService } from './sessionHeartbeatService';
import { clearAdminSession, isAdminSession } from '../constants/adminSession';
import { setAuthRedirectMessage } from './sessionMigration';
import { secureTokenStorage } from '../utils/secureTokenStorage';
import { logger } from '../utils/logger';
import { t } from '../i18n/i18nService';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface EmailCodeLoginRequest {
  email: string;
  code: string;
}

export interface ForgotEmailSendCodeRequest {
  email: string;
}

export interface ForgotEmailResetRequest {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ProfileUpdateRequest {
  username?: string;
  avatarUrl?: string;
  callSign?: string;
}

export interface EmailBindConfirmRequest {
  email: string;
  code: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: number;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  user: User;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  phoneCountryCode?: number;
  phoneNumber?: string;
  organization?: string;
  callSign?: string;
}

export interface User {
  id: number;
  username: string;
  email?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  phoneCountryCodeId?: number;
  callSign?: string;
  status?: string;
}

class AuthService {
  private isHandlingUnauthorized = false;

  map401Message(message?: string): string {
    if (message?.includes('其他设备')) {
      return t('auth.otherDeviceLogin');
    }
    if (message?.includes('会话已过期')) {
      return t('auth.sessionExpired');
    }
    if (message?.includes('账户不可用')) {
      return t('auth.accountUnavailable');
    }
    if (message?.includes('认证服务不可用')) {
      return t('auth.authUnavailable');
    }
    return message || t('auth.loginAgain');
  }

  /**
   * 401 / 410：读 message → 提示 → 清存储 → 断 MQTT → 跳登录
   */
  async handleUnauthorized(serverMessage?: string): Promise<void> {
    if (this.isHandlingUnauthorized) {
      return;
    }
    this.isHandlingUnauthorized = true;

    try {
      const userMessage = this.map401Message(serverMessage);
      logger.warn('认证失效:', userMessage);

      setAuthRedirectMessage(userMessage);
      sessionHeartbeatService.stop();

      try {
        sessionStorage.setItem('wavelink_logged_out', 'true');
      } catch {
        // ignore
      }

      try {
        await logoutCleanupService.performLogoutCleanup({ force: true, silent: true });
      } catch (error) {
        logger.error('401 清理资源失败:', error);
      }

      this.clearAuth();

      window.dispatchEvent(
        new CustomEvent('auth-session-expired', { detail: { message: userMessage } })
      );
      window.dispatchEvent(new CustomEvent('auth-logout'));
    } finally {
      this.isHandlingUnauthorized = false;
    }
  }

  private getLoginFriendlyErrorMessage(
    error: unknown,
    fallback = t('auth.loginError'),
  ): string {
    const isProduction = process.env.NODE_ENV === 'production';
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (isProduction) {
      logger.error('登录错误详情（仅记录）:', { message: errorMessage });
    }

    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return t('auth.networkError');
    }
    if (errorMessage.includes('HTTP error! status: 400') || errorMessage.includes('HTTP error! status: 401')) {
      return t('auth.invalidCredentials');
    }
    if (errorMessage.includes('HTTP error! status: 403') || errorMessage.includes('账户不可用')) {
      return t('auth.accountUnavailable');
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return t('auth.serviceUnavailable');
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return t('auth.serverError');
    }
    if (errorMessage.includes('Redis 不可用')) {
      return t('auth.authUnavailable');
    }
    if (errorMessage.includes('timeout')) {
      return t('auth.requestTimeout');
    }

    if (
      errorMessage.includes('登录失败')
      || errorMessage.toLowerCase().includes('login failed')
    ) {
      return fallback;
    }

    return errorMessage || fallback;
  }

  private getRecoveryFriendlyErrorMessage(error: unknown): string {
    return this.getLoginFriendlyErrorMessage(error, t('auth.operationError'));
  }

  private getProfileFriendlyErrorMessage(
    error: unknown,
    fallback = t('auth.operationError'),
  ): string {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('验证码错误')) {
      return t('auth.invalidOrExpiredCode');
    }
    if (
      errorMessage.includes('已被其他账号绑定')
      || errorMessage.includes('已是当前邮箱')
    ) {
      return t('auth.bindEmailError');
    }
    if (errorMessage.includes('已被占用')) {
      return t('auth.updateError');
    }
    return this.getRecoveryFriendlyErrorMessage(error) || fallback;
  }

  private getEmailCodeSuccessMessage(message?: string): string {
    if (
      !message
      || (message.includes('验证码') && message.includes('发送'))
      || (/verification code/i.test(message) && /sent/i.test(message))
    ) {
      return t('auth.emailCodeSent');
    }
    return message;
  }

  private getPasswordResetSuccessMessage(message?: string): string {
    if (
      !message
      || (message.includes('密码') && message.includes('重置'))
      || (/password/i.test(message) && /reset/i.test(message))
    ) {
      return t('auth.passwordReset');
    }
    return message;
  }

  private normalizeStoredUser(user: User): User {
    return {
      ...user,
      id: typeof user.id === 'string' ? parseInt(user.id, 10) : user.id,
      email: user.email || undefined,
    };
  }

  private persistUserProfile(user: User): User {
    const normalized = this.normalizeStoredUser(user);
    localStorage.setItem(
      'user',
      JSON.stringify({
        ...normalized,
        id: normalized.id.toString(),
        email: normalized.email || '',
      }),
    );
    return normalized;
  }

  private persistLoginResponse(data: LoginResponse): LoginResponse {
    clearAdminSession();
    const userToStore = {
      ...data.user,
      id: data.user.id.toString(),
      email: data.user.email || '',
    };

    secureTokenStorage.setAccessToken(data.accessToken, data.expiresIn);
    localStorage.setItem('user', JSON.stringify(userToStore));

    try {
      sessionStorage.removeItem('wavelink_logged_out');
    } catch (error) {
      console.warn('清除登出标志失败:', error);
    }

    sessionHeartbeatService.start();
    window.dispatchEvent(new CustomEvent('auth-success'));

    return data;
  }

  private async postAuthLogin(
    endpoint: string,
    body: LoginRequest | EmailCodeLoginRequest | Record<string, unknown>,
  ): Promise<LoginResponse> {
    const response = await ApiClient.post<ApiResponse<LoginResponse>>(
      endpoint,
      body,
      { skipAuth: true, skip401Handler: true },
    );

    if (!response.success) {
      throw new Error(response.message || t('auth.loginFailed'));
    }

    return this.persistLoginResponse(response.data);
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      return await this.postAuthLogin('/users/login', credentials);
    } catch (error) {
      logger.error('Login error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error));
    }
  }

  async loginByEmail(credentials: EmailCodeLoginRequest): Promise<LoginResponse> {
    try {
      return await this.postAuthLogin('/users/login/email', {
        email: credentials.email.trim().toLowerCase(),
        code: credentials.code.trim(),
      });
    } catch (error) {
      logger.error('Email code login error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error, t('auth.invalidOrExpiredCode')));
    }
  }

  async sendLoginEmailCode(email: string): Promise<string> {
    try {
      const response = await ApiClient.post<ApiResponse<unknown>>(
        '/users/login/email/send-code',
        { email: email.trim().toLowerCase() },
        { skipAuth: true, skip401Handler: true },
      );
      if (!response.success) {
        throw new Error(response.message || t('auth.sendCodeFailed'));
      }
      return this.getEmailCodeSuccessMessage(response.message);
    } catch (error) {
      logger.error('Send login email code error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error, t('auth.sendCodeFailed')));
    }
  }

  async sendForgotPasswordEmailCode(email: string): Promise<string> {
    try {
      const response = await ApiClient.post<ApiResponse<unknown>>(
        '/users/password/forgot/email/send-code',
        { email: email.trim().toLowerCase() },
        { skipAuth: true, skip401Handler: true },
      );
      if (!response.success) {
        throw new Error(response.message || t('auth.sendCodeFailed'));
      }
      return this.getEmailCodeSuccessMessage(response.message);
    } catch (error) {
      logger.error('Send forgot email code error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error, t('auth.sendCodeFailed')));
    }
  }

  async resetPasswordByEmail(payload: ForgotEmailResetRequest): Promise<string> {
    try {
      const response = await ApiClient.post<ApiResponse<unknown>>(
        '/users/password/forgot/email/reset',
        {
          email: payload.email.trim().toLowerCase(),
          code: payload.code.trim(),
          newPassword: payload.newPassword,
          confirmPassword: payload.confirmPassword,
        },
        { skipAuth: true, skip401Handler: true },
      );
      if (!response.success) {
        throw new Error(response.message || t('auth.resetError'));
      }
      return this.getPasswordResetSuccessMessage(response.message);
    } catch (error) {
      logger.error('Reset password by email error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error, t('auth.resetError')));
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();

      if (token) {
        try {
          await ApiClient.post<ApiResponse<string>>(
            '/users/logout',
            null,
            {
              headers: { Authorization: `Bearer ${token}` },
              skip401Handler: true,
            }
          );
        } catch {
          logger.debug('登出完成（本地清理）');
        }
      }
    } catch (error) {
      logger.error('登出过程中发生错误:', error);
    } finally {
      sessionHeartbeatService.stop();

      try {
        sessionStorage.setItem('wavelink_logged_out', 'true');
      } catch (error) {
        logger.warn('设置登出标志失败:', error);
      }

      window.dispatchEvent(new CustomEvent('auth-logout-start'));
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        await logoutCleanupService.performLogoutCleanup({ force: true, silent: false });
      } catch (error) {
        logger.error('资源清理失败:', error);
      }

      try {
        this.clearAuth();
      } catch (error) {
        logger.error('清理本地存储失败:', error);
      }

      setAuthRedirectMessage(t('auth.safeLogout'));
      window.dispatchEvent(new CustomEvent('auth-logout'));
    }
  }

  getToken(): string | null {
    return secureTokenStorage.getAccessToken();
  }

  getSessionExpiresAt(): number | null {
    return secureTokenStorage.getSessionExpiresAt();
  }

  clearAuth(): void {
    secureTokenStorage.clearTokens();
    localStorage.removeItem('user');
    clearAdminSession();
  }

  getUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      const storedUser = JSON.parse(userStr);

      if (!storedUser || !storedUser.id || !storedUser.username) {
        logger.warn('用户信息格式不正确，清除无效数据');
        localStorage.removeItem('user');
        return null;
      }

      return {
        ...storedUser,
        id: parseInt(storedUser.id, 10),
        email: storedUser.email || undefined,
      };
    } catch (error) {
      logger.error('解析用户信息失败:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  isAuthenticated(): boolean {
    if (isAdminSession()) {
      return false;
    }
    return !!this.getToken();
  }

  private getUserFriendlyErrorMessage(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return t('auth.networkError');
    }
    if (errorMessage.includes('HTTP error! status: 400')) {
      return t('auth.badRequest');
    }
    if (errorMessage.includes('HTTP error! status: 401')) {
      return t('auth.authenticationFailed');
    }
    if (errorMessage.includes('HTTP error! status: 403')) {
      return t('auth.forbidden');
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return t('auth.serviceUnavailable');
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return t('auth.serverError');
    }
    if (errorMessage.includes('username') && errorMessage.includes('already exists')) {
      return t('auth.usernameExists');
    }
    if (errorMessage.includes('email') && errorMessage.includes('already exists')) {
      return t('auth.emailExists');
    }
    if (errorMessage.includes('timeout')) {
      return t('auth.requestTimeout');
    }

    if (
      errorMessage.includes('Registration failed')
      || errorMessage.includes('注册失败')
    ) {
      return t('auth.registrationError');
    }
    return errorMessage || t('auth.registrationError');
  }

  async register(userData: RegisterRequest): Promise<void> {
    try {
      const response = await ApiClient.post<ApiResponse<{ success: boolean; message?: string }>>(
        '/users/register',
        userData,
        { skipAuth: true, skip401Handler: true }
      );

      if (!response.success) {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      logger.error('Registration error:', error);
      throw new Error(this.getUserFriendlyErrorMessage(error));
    }
  }

  async fetchCurrentUser(): Promise<User> {
    try {
      const response = await ApiClient.get<ApiResponse<User>>('/users/me');
      if (!response.success) {
        throw new Error(response.message || t('auth.profileLoadError'));
      }
      return this.persistUserProfile(response.data);
    } catch (error) {
      logger.error('Fetch current user error:', error);
      throw new Error(this.getProfileFriendlyErrorMessage(error, t('auth.profileLoadError')));
    }
  }

  async updateProfile(userId: number, payload: ProfileUpdateRequest): Promise<User> {
    try {
      const body: ProfileUpdateRequest = {};
      if (payload.username !== undefined) {
        body.username = payload.username.trim();
      }
      if (payload.avatarUrl !== undefined) {
        body.avatarUrl = payload.avatarUrl.trim();
      }
      if (payload.callSign !== undefined) {
        body.callSign = payload.callSign.trim();
      }

      const response = await ApiClient.put<ApiResponse<User>>(`/users/${userId}`, body);
      if (!response.success) {
        throw new Error(response.message || t('auth.updateError'));
      }
      return this.persistUserProfile(response.data);
    } catch (error) {
      logger.error('Update profile error:', error);
      throw new Error(this.getProfileFriendlyErrorMessage(error, t('auth.profileUpdateError')));
    }
  }

  async sendEmailBindCode(userId: number, email: string): Promise<string> {
    try {
      const response = await ApiClient.post<ApiResponse<unknown>>(
        `/users/${userId}/email/bind/send-code`,
        { email: email.trim().toLowerCase() },
      );
      if (!response.success) {
        throw new Error(response.message || t('auth.sendCodeFailed'));
      }
      return response.message || t('auth.emailCodeToNewAddress');
    } catch (error) {
      logger.error('Send email bind code error:', error);
      throw new Error(this.getProfileFriendlyErrorMessage(error, t('auth.sendCodeFailed')));
    }
  }

  async confirmEmailBind(userId: number, payload: EmailBindConfirmRequest): Promise<User> {
    try {
      const response = await ApiClient.post<ApiResponse<User>>(
        `/users/${userId}/email/bind/confirm`,
        {
          email: payload.email.trim().toLowerCase(),
          code: payload.code.trim(),
        },
      );
      if (!response.success) {
        throw new Error(response.message || t('auth.bindEmailError'));
      }
      return this.persistUserProfile(response.data);
    } catch (error) {
      logger.error('Confirm email bind error:', error);
      throw new Error(this.getProfileFriendlyErrorMessage(error, t('auth.invalidOrExpiredCode')));
    }
  }
}

export const authService = new AuthService();
